// Decorative backdrop used by main screens (Discover, Library, Matches,
// Flashcard, HSK home). Renders the cloud-lattice (云纹) pattern overlay plus
// a single oversized Hanzi watermark that breathes slowly behind content.
// The wrapping element is absolute + pointer-events:none, so it can sit
// inside any positioned parent without affecting hit testing.

import type { CSSProperties } from 'react';

type Corner = 'tr' | 'tl' | 'br' | 'bl';
type Tint = 'vermillion' | 'gold';

interface ScreenBackdropProps {
  /** Character(s) to render as the watermark. */
  mark: string;
  /** Which corner to anchor the watermark to. Defaults to top-right. */
  position?: Corner;
  /** Watermark color. Defaults to vermillion. */
  tint?: Tint;
  /** Approximate pixel size of the watermark glyph. Defaults to 220. */
  size?: number;
  /** Rotation in degrees applied to the watermark. Defaults to -8. */
  rotate?: number;
  /** Disable the lattice background — useful on screens where the pattern would compete with content. */
  withoutPattern?: boolean;
}

const POSITION_STYLES: Record<Corner, CSSProperties> = {
  tr: { top: 60, right: -20 },
  tl: { top: 60, left: -30 },
  br: { bottom: 80, right: -20 },
  bl: { bottom: 80, left: -30 },
};

export function ScreenBackdrop({
  mark,
  position = 'tr',
  tint = 'vermillion',
  size = 220,
  rotate = -8,
  withoutPattern = false,
}: ScreenBackdropProps) {
  return (
    <>
      {!withoutPattern && (
        <div
          className="pointer-events-none absolute inset-0 bg-yunwen"
          style={{ zIndex: -1 }}
          aria-hidden
        />
      )}
      <div
        className="kw-watermark anim-breathe"
        style={{
          ...POSITION_STYLES[position],
          fontSize: `${size}px`,
          transform: `rotate(${rotate}deg)`,
          color: tint === 'gold' ? '#C9A36A' : '#E94B3C',
        }}
        aria-hidden
      >
        {mark}
      </div>
    </>
  );
}
